import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to clear user data
const clearUserData = () => {
  localStorage.removeItem("firstName");
  localStorage.removeItem("lastName");
  localStorage.removeItem("userName");
  localStorage.removeItem("email");
  localStorage.removeItem("phone");
  localStorage.removeItem("profilePicture");
  localStorage.removeItem("userType");
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("unreadNotifications");
  localStorage.removeItem("unreadMessages");
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const queryClient = useQueryClient();

  // Logout function - defined first to avoid hoisting issues
  const logout = useCallback(async (performApiCall = true) => {
    if (performApiCall && token) {
      try {
        await authService.signOut();
      } catch (error) {
        console.error("Error during API sign out:", error);
      }
    }

    // Clear state
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    
    // Clear localStorage
    clearUserData();
  }, [token]);

  // Validate token and get fresh user data
  const validateToken = useCallback(async (tokenToValidate = token) => {
    if (!tokenToValidate) {
      logout();
      return false;
    }

    // Basic token format validation
    if (!tokenToValidate.match(/^[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*$/)) {
      console.log("Invalid token format");
      logout();
      return false;
    }

    try {
      const response = await authService.validateToken();
      const data = response.data;
      
      if (data.user) {
        // Update localStorage with fresh user data
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("token", tokenToValidate);
        localStorage.setItem("profilePicture", data.user.profilePicture || "");
        localStorage.setItem("userType", data.user.accountType || "");
        localStorage.setItem("firstName", data.user.firstName || "");
        localStorage.setItem("lastName", data.user.lastName || "");
        localStorage.setItem("userName", data.user.userName || "");
        localStorage.setItem("email", data.user.email || "");
        localStorage.setItem("phone", data.user.phone || "");
        localStorage.setItem("unreadNotifications", data.user.metaData?.unreadNotifications || 0);
        localStorage.setItem("unreadMessages", data.user.metaData?.unreadMessages || 0);

        // Update state
        setUser(data.user);
        setToken(tokenToValidate);
        setIsAuthenticated(true);
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error("Error validating token:", error);
      logout();
      return false;
    }
  }, [token, logout]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (storedToken && storedUser) {
        setToken(storedToken);
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setIsAuthenticated(true);
          
          // Validate token by fetching fresh user data
          await validateToken(storedToken);
        } catch (error) {
          console.error("Error parsing stored user data:", error);
          logout();
        }
      } else if (storedToken) {
        // Token exists but no user data, fetch user data
        await validateToken(storedToken);
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, [logout, validateToken]);

  // Login function
  const login = async (loginToken, userData = null) => {
    setToken(loginToken);
    localStorage.setItem("token", loginToken);
    
    if (userData) {
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem("user", JSON.stringify(userData));
    } else {
      // Fetch user data with the new token
      const isValid = await validateToken(loginToken);
      if (!isValid) {
        return false;
      }
    }
    
    return true;
  };

  // Check if user is authenticated
  const checkAuth = () => {
    const storedToken = localStorage.getItem("token");
    return !!storedToken && isAuthenticated;
  };

  // Get user data from state or localStorage
  const getUserData = () => {
    if (user) return user;
    
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        return null;
      }
    }
    
    return null;
  };

  // Update user data
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  // Refresh user data
  const refreshUser = async () => {
    if (token) {
      return await validateToken(token);
    }
    return false;
  };

  const value = {
    isAuthenticated,
    user,
    token,
    loading,
    login,
    logout,
    checkAuth,
    validateToken,
    getUserData,
    updateUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
